import { Connection, PublicKey } from '@solana/web3.js';
import UniversalProvider from '@walletconnect/universal-provider';
import bs58 from 'bs58';
import {} from '@reown/appkit';
import { ConstantsUtil as CommonConstantsUtil } from '@reown/appkit-common';
import { AlertController, ChainController, CoreHelperUtil, StorageUtil } from '@reown/appkit-core';
import { ErrorUtil } from '@reown/appkit-utils';
import { SolConstantsUtil } from '@reown/appkit-utils/solana';
import { W3mFrameProvider } from '@reown/appkit-wallet';
import { AdapterBlueprint } from '@reown/appkit/adapters';
import { AuthProvider } from './providers/AuthProvider.js';
import { CoinbaseWalletProvider } from './providers/CoinbaseWalletProvider.js';
import { SolanaWalletConnectProvider } from './providers/SolanaWalletConnectProvider.js';
import { SolStoreUtil } from './utils/SolanaStoreUtil.js';
import { createSendTransaction } from './utils/createSendTransaction.js';
import { watchStandard } from './utils/watchStandard.js';
export class SolanaAdapter extends AdapterBlueprint {
    constructor(options = {}) {
        super({});
        this.adapterType = 'solana';
        this.balancePromises = {};
        this.providerHandlers = null;
        this.namespace = CommonConstantsUtil.CHAIN.SOLANA;
        this.connectionSettings = options.connectionSettings || 'confirmed';
        this.wallets = options.wallets;
    }
    setAuthProvider(w3mFrameProvider) {
        this.addConnector(new AuthProvider({
            w3mFrameProvider,
            getActiveChain: () => ChainController.state.activeCaipNetwork,
            chains: this.caipNetworks
        }));
    }
    syncConnectors(options, appKit) {
        if (!options.projectId) {
            AlertController.open(ErrorUtil.ALERT_ERRORS.PROJECT_ID_NOT_CONFIGURED, 'error');
        }
        const getActiveChain = () => appKit.getCaipNetwork(this.namespace);
        if (CoreHelperUtil.isClient() && 'coinbaseSolana' in window) {
            this.addConnector(new CoinbaseWalletProvider({
                provider: window.coinbaseSolana,
                chains: this.caipNetworks,
                getActiveChain
            }));
        }
        watchStandard(this.caipNetworks, getActiveChain, this.addConnector.bind(this));
    }
    async getEnsAddress(params) {
        return { address: params.name };
    }
    async writeContract() {
        return Promise.resolve({
            hash: ''
        });
    }
    async getCapabilities() {
        return Promise.resolve({});
    }
    async grantPermissions() {
        return Promise.resolve({});
    }
    async revokePermissions() {
        return Promise.resolve('0x');
    }
    async walletGetAssets(_params) {
        return Promise.resolve({});
    }
    async getAccounts(params) {
        const connector = this.connectors.find(c => c.id === params.id);
        if (!connector) {
            return { accounts: [] };
        }
        return { accounts: await connector.getAccounts() };
    }
    async signMessage(params) {
        const provider = params.provider;
        if (!provider) {
            throw new Error('connectionControllerClient:signMessage - provider is undefined');
        }
        const signature = await provider.signMessage(new TextEncoder().encode(params.message));
        return {
            signature: bs58.encode(signature)
        };
    }
    async estimateGas(params) {
        const connection = SolStoreUtil.state.connection;
        if (!connection || !params.provider) {
            throw new Error('Connection is not set');
        }
        const transaction = await createSendTransaction({
            provider: params.provider,
            connection,
            to: '11111111111111111111111111111111',
            value: 1
        });
        const fee = await transaction.getEstimatedFee(connection);
        return {
            gas: BigInt(fee || 0)
        };
    }
    async sendTransaction(params) {
        const connection = SolStoreUtil.state.connection;
        if (!connection || !params.address || !params.provider) {
            throw new Error('Connection is not set');
        }
        const provider = params.provider;
        const transaction = await createSendTransaction({
            provider,
            connection,
            to: params.to,
            value: params.value
        });
        const result = await provider.sendTransaction(transaction, connection);
        await new Promise(resolve => {
            const interval = setInterval(async () => {
                const status = await connection.getSignatureStatus(result);
                if (status?.value) {
                    clearInterval(interval);
                    resolve();
                }
            }, 1000);
        });
        return {
            hash: result
        };
    }
    parseUnits() {
        return 0n;
    }
    formatUnits() {
        return '';
    }
    async connect(params) {
        const connector = this.connectors.find(c => c.id === params.id);
        if (!connector) {
            throw new Error('Provider not found');
        }
        const rpcUrl = params.rpcUrl ||
            this.caipNetworks?.find(n => n.id === params.chainId)?.rpcUrls.default.http[0];
        if (!rpcUrl) {
            throw new Error(`RPC URL not found for chainId: ${params.chainId}`);
        }
        const address = await connector.connect({
            chainId: params.chainId
        });
        this.listenProviderEvents(connector);
        SolStoreUtil.setConnection(new Connection(rpcUrl, this.connectionSettings));
        return {
            id: connector.id,
            address,
            chainId: params.chainId,
            provider: connector,
            type: connector.type
        };
    }
    async getBalance(params) {
        const connection = new Connection(params.caipNetwork?.rpcUrls?.default?.http?.[0], this.connectionSettings);
        const caipAddress = `${params?.caipNetwork?.caipNetworkId}:${params.address}`;
        const cachedPromise = this.balancePromises[caipAddress];
        if (cachedPromise) {
            return cachedPromise;
        }
        const cachedBalance = StorageUtil.getNativeBalanceCacheForCaipAddress(caipAddress);
        if (cachedBalance) {
            return { balance: cachedBalance.balance, symbol: cachedBalance.symbol };
        }
        this.balancePromises[caipAddress] = new Promise(async (resolve) => {
            const balance = await connection.getBalance(new PublicKey(params.address));
            const formattedBalance = (balance / SolConstantsUtil.LAMPORTS_PER_SOL).toString();
            StorageUtil.updateNativeBalanceCache({
                caipAddress,
                balance: formattedBalance,
                symbol: params.caipNetwork?.nativeCurrency.symbol || 'SOL',
                timestamp: Date.now()
            });
            if (!params.caipNetwork) {
                throw new Error('caipNetwork is required');
            }
            resolve({
                balance: formattedBalance,
                symbol: params.caipNetwork?.nativeCurrency.symbol
            });
        }).finally(() => {
            delete this.balancePromises[caipAddress];
        });
        return this.balancePromises[caipAddress] || { balance: '0', symbol: 'SOL' };
    }
    async switchNetwork(params) {
        await super.switchNetwork(params);
        const { caipNetwork } = params;
        if (caipNetwork?.rpcUrls?.default?.http?.[0]) {
            SolStoreUtil.setConnection(new Connection(caipNetwork.rpcUrls.default.http[0], this.connectionSettings));
        }
    }
    listenProviderEvents(provider) {
        const disconnectHandler = () => {
            this.removeProviderListeners(provider);
            this.emit('disconnect');
        };
        const accountsChangedHandler = (publicKey) => {
            const address = publicKey.toBase58();
            if (address) {
                this.emit('accountChanged', { address });
            }
        };
        provider.on('disconnect', disconnectHandler);
        provider.on('accountsChanged', accountsChangedHandler);
        provider.on('connect', accountsChangedHandler);
        provider.on('pendingTransaction', () => {
            this.emit('pendingTransactions');
        });
        this.providerHandlers = {
            disconnect: disconnectHandler,
            accountsChanged: accountsChangedHandler
        };
    }
    removeProviderListeners(provider) {
        if (this.providerHandlers) {
            provider.removeListener('disconnect', this.providerHandlers.disconnect);
            provider.removeListener('accountsChanged', this.providerHandlers.accountsChanged);
            provider.removeListener('connect', this.providerHandlers.accountsChanged);
            this.providerHandlers = null;
        }
    }
    setUniversalProvider(universalProvider) {
        this.addConnector(new SolanaWalletConnectProvider({
            provider: universalProvider,
            chains: this.caipNetworks,
            getActiveChain: () => ChainController.state.activeCaipNetwork
        }));
    }
    async connectWalletConnect(chainId) {
        const result = await super.connectWalletConnect(chainId);
        const rpcUrl = this.caipNetworks?.find(n => n.id === chainId)?.rpcUrls.default.http[0];
        const connection = new Connection(rpcUrl, this.connectionSettings);
        SolStoreUtil.setConnection(connection);
        return result;
    }
    async disconnect(params) {
        if (!params.provider || !params.providerType) {
            throw new Error('Provider or providerType not provided');
        }
        await params.provider.disconnect();
    }
    async getProfile() {
        return Promise.resolve({
            profileName: undefined,
            profileImage: undefined
        });
    }
    async syncConnection(params) {
        return this.connect({
            ...params,
            type: ''
        });
    }
    getWalletConnectProvider(params) {
        const walletConnectProvider = new SolanaWalletConnectProvider({
            provider: params.provider,
            chains: params.caipNetworks,
            getActiveChain: () => ChainController.state.activeCaipNetwork
        });
        return walletConnectProvider;
    }
}
//# sourceMappingURL=client.js.map